/**
 * StructuredView — renders a buildView() ViewModel (spec §17). Owns UI state
 * only: collapsed sections, shown empties, table state, revealed values,
 * flash / focus. Every data decision was made in buildView.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";

import colors, { status } from "../../../../../../../theme/colors";
import { resolveFocus } from "../buildView";
import { Blocks, ChecksList, LinkButton } from "./blocks";
import { DEFAULT_TABLE_STATE } from "./DataTable";
import { APP_FONT, FOCUS_RING, INK, LINE, MONO, SUB, domId, sectionDomId } from "./tokens";

const BAR_H = 44;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Walk blocks, calling fn(block, ancestors). */
const walkBlocks = (blocks, fn, ancestors = []) =>
  blocks.forEach((b) => {
    fn(b, ancestors);
    if (b.kind === "object") walkBlocks(b.blocks, fn, [...ancestors, b]);
    if (b.kind === "table") b.rows.forEach((r) => walkBlocks(r.detail || [], fn, [...ancestors, b]));
  });

const Section = ({ id, title, meta, open, onToggle, children }) => (
  <Box id={sectionDomId(id)} component="section" sx={{ borderTop: `1px solid ${LINE}`, "&:first-of-type": { borderTop: 0 } }}>
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "baseline",
        gap: 1,
        px: 2,
        pt: 1.75,
        pb: open ? 1 : 1.75,
        textAlign: "left",
        "&:focus-visible": { ...FOCUS_RING, outlineOffset: "-2px" },
      }}
    >
      <Box component="span" sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{title}</Box>
      {meta ? <Box component="span" sx={{ fontSize: 12, color: SUB }}>{meta}</Box> : null}
      <KeyboardArrowDownRounded
        sx={{
          ml: "auto",
          alignSelf: "center",
          fontSize: 18,
          color: SUB,
          transition: "transform .15s",
          transform: open ? "none" : "rotate(-90deg)",
        }}
      />
    </ButtonBase>
    {open ? <Box sx={{ px: 1.25, pb: 2 }}>{children}</Box> : null}
  </Box>
);

const JumpBar = ({ pills, active, onJump }) => (
  <Box
    component="nav"
    aria-label="Sections"
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 2,
      height: BAR_H,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.5,
      overflowX: "auto",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
      backgroundColor: colors.white,
      borderBottom: `1px solid ${LINE}`,
    }}
  >
    {pills.map((p) => {
      const isActive = p.id === active;
      return (
        <ButtonBase
          key={p.id}
          onClick={() => onJump(p.id)}
          aria-current={isActive ? "true" : undefined}
          sx={{
            flex: "none",
            gap: 0.625,
            px: 1.25,
            py: 0.5,
            borderRadius: "999px",
            fontSize: 12,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? colors.white : colors.slate600,
            backgroundColor: isActive ? INK : "transparent",
            "&:hover": { backgroundColor: isActive ? INK : colors.slate100 },
            "&:focus-visible": FOCUS_RING,
          }}
        >
          {p.title}
          {p.badge ? (
            <Box
              component="span"
              sx={{
                minWidth: 16,
                height: 16,
                px: 0.5,
                borderRadius: "999px",
                fontSize: 10.5,
                fontWeight: 700,
                lineHeight: "16px",
                textAlign: "center",
                color: status.warning.fg,
                backgroundColor: status.warning.bg,
              }}
            >
              {p.badge}
            </Box>
          ) : null}
        </ButtonBase>
      );
    })}
  </Box>
);

export default function StructuredView({ view, focusField, onShowInText, onFocusResolved, onRevealSensitive }) {
  const scrollRef = useRef(null);
  const flashTimer = useRef(null);
  const [collapsed, setCollapsed] = useState(
    () => new Set([...view.sections.filter((s) => s.defaultCollapsed).map((s) => s.id), "details"]),
  );
  const [showEmpty, setShowEmpty] = useState(() => new Set());
  const [revealed, setRevealed] = useState(() => new Set());
  const [openObjects, setOpenObjects] = useState(() => new Set());
  const [closedObjects, setClosedObjects] = useState(() => new Set());
  const [tables, setTables] = useState({});
  const [flash, setFlash] = useState(null);
  const [active, setActive] = useState(null);
  const [focusMissing, setFocusMissing] = useState(false);

  const openChecks = view.checks.filter((c) => c.tone !== "ok").length;
  const pills = useMemo(
    () => [
      ...(view.checks.length ? [{ id: "checks", title: "Checks", badge: openChecks || undefined }] : []),
      ...view.sections.map((s) => ({ id: s.id, title: s.title, badge: s.badge })),
      { id: "details", title: "Details" },
    ],
    [view, openChecks],
  );

  // Section + table lookups for goTo.
  const index = useMemo(() => {
    const tablesByPath = {};
    const objects = [];
    const sectionOfPath = {};
    const emptyPaths = new Set();
    view.sections.forEach((s) =>
      walkBlocks(s.blocks, (b) => {
        sectionOfPath[b.path] = s.id;
        if (b.kind === "table") {
          tablesByPath[b.path] = b;
          b.rows.forEach((r) => (sectionOfPath[r.path] = s.id));
        }
        if (b.kind === "object") objects.push(b);
        if (b.kind === "kv" && b.empty) emptyPaths.add(b.path);
      }),
    );
    return { tablesByPath, objects, sectionOfPath, emptyPaths };
  }, [view]);

  const setTable = useCallback(
    (path, patch) => setTables((prev) => ({ ...prev, [path]: { ...DEFAULT_TABLE_STATE, ...(prev[path] || {}), ...patch } })),
    [],
  );

  const scrollToEl = useCallback((el, center) => {
    const box = scrollRef.current;
    if (!el || !box) return;
    const smooth = !reducedMotion();
    if (center) {
      el.scrollIntoView({ block: "center", behavior: smooth ? "smooth" : "auto" });
    } else {
      const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - BAR_H;
      box.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
    }
  }, []);

  const goTo = useCallback(
    (target, mode = "flash") => {
      if (!target) return;
      const path = target.path || null;
      const sectionId =
        target.sectionId ||
        (path && (index.sectionOfPath[path] || Object.entries(index.sectionOfPath).find(([p]) => path.startsWith(`${p}.`) || path.startsWith(`${p}[`))?.[1]));
      if (sectionId) {
        setCollapsed((prev) => {
          if (!prev.has(sectionId)) return prev;
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
      }
      // Rows inside a table: clear search, show the group, page far enough.
      const table = Object.values(index.tablesByPath).find(
        (t) =>
          (path && (path.startsWith(`${t.path}[`))) ||
          (target.expandGroup && (t.groups || []).some((g) => g.id === target.expandGroup)),
      );
      if (table) {
        const row = path ? table.rows.find((r) => path === r.path || path.startsWith(`${r.path}.`)) : null;
        const groupId = target.expandGroup || row?.groupId;
        const group = (table.groups || []).find((g) => g.id === groupId);
        setTables((prev) => {
          const st = { ...DEFAULT_TABLE_STATE, ...(prev[table.path] || {}) };
          let toggled = st.toggled;
          if (group) {
            toggled =
              group.kind === "empty"
                ? toggled.includes(group.id) ? toggled : [...toggled, group.id]
                : toggled.filter((x) => x !== group.id);
          }
          const rows = row && path !== row.path && !st.rows.includes(row.path) ? [...st.rows, row.path] : st.rows;
          return {
            ...prev,
            [table.path]: { ...st, query: "", view: table.groups ? "grouped" : st.view, toggled, rows, limit: Math.max(st.limit, table.rows.length) },
          };
        });
      }
      // Collapsed object groups on the way down.
      if (path) {
        const ancestors = index.objects.filter((o) => path === o.path || path.startsWith(`${o.path}.`) || path.startsWith(`${o.path}[`));
        if (ancestors.length) {
          setOpenObjects((prev) => new Set([...prev, ...ancestors.map((o) => o.path)]));
          setClosedObjects((prev) => {
            const next = new Set(prev);
            ancestors.forEach((o) => next.delete(o.path));
            return next;
          });
        }
        if (index.emptyPaths.has(path) && sectionId) setShowEmpty((prev) => new Set(prev).add(sectionId));
      }
      clearTimeout(flashTimer.current);
      setFlash(path ? { path, mode } : null);
      if (mode === "flash") flashTimer.current = setTimeout(() => setFlash(null), 1500);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const el = path ? document.getElementById(domId(path)) : null;
          if (el) scrollToEl(el, true);
          else if (sectionId) scrollToEl(document.getElementById(sectionDomId(sectionId)), false);
        }),
      );
    },
    [index, scrollToEl],
  );

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // focusField (spec §14): resolve once per view.
  useEffect(() => {
    setFocusMissing(false);
    if (!focusField) return;
    const entry = resolveFocus(view.focusIndex, focusField);
    if (!entry) {
      setFocusMissing(true);
      return;
    }
    goTo({ sectionId: entry.sectionId, path: entry.path || undefined }, "focus");
    onFocusResolved?.(entry.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, focusField]);

  // Scroll spy for the jump bar.
  const onScroll = useCallback(() => {
    const box = scrollRef.current;
    if (!box) return;
    if (box.scrollTop + box.clientHeight >= box.scrollHeight - 2) {
      setActive(pills[pills.length - 1]?.id);
      return;
    }
    const boxTop = box.getBoundingClientRect().top;
    let current = pills[0]?.id;
    pills.forEach((p) => {
      const el = document.getElementById(sectionDomId(p.id));
      if (el && el.getBoundingClientRect().top - boxTop <= BAR_H + 60) current = p.id;
    });
    setActive(current);
  }, [pills]);
  useEffect(() => {
    onScroll();
  }, [onScroll]);

  const jump = (id) => {
    setCollapsed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    requestAnimationFrame(() => scrollToEl(document.getElementById(sectionDomId(id)), false));
  };

  const toggle = (setter, id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const ui = {
    revealed,
    openObjects,
    closedObjects,
    tables,
    flash,
    canShowInText: view.canShowInText,
  };
  const actions = {
    goTo,
    setTable,
    showInText: onShowInText,
    toggleReveal: (path) => {
      if (!revealed.has(path)) onRevealSensitive?.(path);
      toggle(setRevealed, path);
    },
    toggleObject: (path, collapsedByDefault) => toggle(collapsedByDefault ? setOpenObjects : setClosedObjects, path),
  };

  return (
    <Box
      ref={scrollRef}
      onScroll={onScroll}
      onMouseDown={() => flash?.mode === "focus" && setFlash(null)}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        position: "relative",
        backgroundColor: colors.white,
        ...APP_FONT,
      }}
    >
      <JumpBar pills={pills} active={active} onJump={jump} />
      <Box sx={{ maxWidth: 820, mx: "auto" }}>
        {focusMissing ? (
          <Box sx={{ mx: 2, mt: 1.5, px: 1.25, py: 0.75, fontSize: 12, borderRadius: "6px", color: status.warning.fg, backgroundColor: status.warning.bg }}>
            Couldn't find "{focusField}" in this extract.
          </Box>
        ) : null}

        {view.checks.length ? (
          <Section
            id="checks"
            title="Checks"
            meta={openChecks ? `${openChecks} to review` : "All clear"}
            open={!collapsed.has("checks")}
            onToggle={() => toggle(setCollapsed, "checks")}
          >
            <Box sx={{ px: 0.75 }}>
              <ChecksList checks={view.checks} onGo={(t) => goTo(t)} />
            </Box>
          </Section>
        ) : null}

        {view.sections.map((s) => {
          const emptyShown = showEmpty.has(s.id);
          return (
            <Section
              key={s.id}
              id={s.id}
              title={s.title}
              meta={s.meta}
              open={!collapsed.has(s.id)}
              onToggle={() => toggle(setCollapsed, s.id)}
            >
              <Blocks blocks={s.blocks} ui={ui} actions={actions} hideEmpty={!emptyShown} />
              {s.emptyCount ? (
                <LinkButton onClick={() => toggle(setShowEmpty, s.id)} sx={{ ml: 1, mt: 0.5 }} aria-expanded={emptyShown}>
                  {emptyShown ? `Hide ${s.emptyCount} not found` : `Show ${s.emptyCount} not found`}
                </LinkButton>
              ) : null}
            </Section>
          );
        })}

        <Section
          id="details"
          title="Extraction details"
          open={!collapsed.has("details")}
          onToggle={() => toggle(setCollapsed, "details")}
        >
          <Box
            component="dl"
            sx={{ m: 0, px: 1, display: "grid", gridTemplateColumns: "minmax(110px, 34%) 1fr", columnGap: 1.5, rowGap: 0.75 }}
          >
            {view.details.map((d, i) => (
              <Box key={`${d.label}-${i}`} sx={{ display: "contents" }}>
                <Box component="dt" sx={{ fontSize: 12.5, color: SUB }}>{d.label}</Box>
                <Box component="dd" sx={{ m: 0, fontFamily: MONO, fontSize: 12, color: INK, overflowWrap: "anywhere" }}>
                  {String(d.value)}
                </Box>
              </Box>
            ))}
          </Box>
        </Section>
      </Box>
    </Box>
  );
}
